import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import FormInput from '../../../components/form/FormInput';

import {
  aiPhoneNumberAdd,
  aiPhoneNumberUpdate,
  trunkOptionGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormSelect from '../../../components/form/FormSelect';

const AddEditAIPhoneNumberModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord,
  formType = 'twilio'
}) => {
  const dispatch = useDispatch();
  const [trunkOptions, setTrunkOptions] = useState([]);
  /* ================= INITIAL VALUES ================= */

  const twilioInitialValues = {
    phone_number: '',
    account_sid: '',
    account_token: '',
    name: ''
  };

  const exotelInitialValues = {
    exotel_phone_number: '',
    exotel_api_key: '',
    exotel_api_token: '',
    exotel_subdomain: '',
    exotel_account_sid: '',
    exotel_app_id: '',
    name: ''
  };

  const sipInitialValues = {
    name: '',
    phone_number: '',
    sip_host: '',
    sip_trunk_name: '',
    sip_trunk_id: '',
    sip_port: 5060,
    sip_username: '',
    sip_password: ''
  };

  const getInitialValues = () => {
    if (isEdit && editRecord) {
      return {
        name: editRecord?.name || ''
      };
    }

    if (formType === 'twilio') return twilioInitialValues;
    if (formType === 'exotel') return exotelInitialValues;
    return sipInitialValues;
  };

  const getTitle = () => {
    if (isEdit && editRecord) {
      return 'Edit Phone Number Name';
    }

    if (formType === 'twilio') return 'Import from Twilio';
    if (formType === 'exotel') return 'Import from Exotel';
    return 'Import from SIP Trunk';
  };

  /* ================= VALIDATION ================= */

  const getValidationSchema = () => {
    // Edit mode → only name required
    if (isEdit) {
      return Yup.object({
        name: Yup.string().required('Name is required')
      });
    }

    // Add mode validations
    if (formType === 'twilio') {
      return Yup.object({
        phone_number: Yup.string().required('Phone number is required'),
        account_sid: Yup.string().required('Account SID is required'),
        account_token: Yup.string().required('Account Token is required'),
        name: Yup.string().required('Name is required')
      });
    }

    if (formType === 'exotel') {
      return Yup.object({
        exotel_phone_number: Yup.string().required('Phone number is required'),
        exotel_api_key: Yup.string().required('API Key is required'),
        exotel_api_token: Yup.string().required('API Token is required'),
        exotel_subdomain: Yup.string().required('Subdomain is required'),
        exotel_account_sid: Yup.string().required('Account SID is required'),
        exotel_app_id: Yup.string().required('App ID is required'),
        name: Yup.string().required('Name is required')
      });
    }

    // SIP
    return Yup.object({
      phone_number: Yup.string().required('Phone number is required'),
      sip_host: Yup.string().required('SIP Host is required'),
      sip_trunk_name: Yup.string().required('SIP Trunk Name is required'),
      sip_trunk_id: Yup.string().required('SIP Trunk is required'),
      name: Yup.string().required('Name is required')
    });
  };

  /* ================= FORM SUBMIT ================= */

  const onSubmit = async (values, { resetForm }) => {
    try {
      if (isEdit) {
        await dispatch(
          aiPhoneNumberUpdate({
            phoneNumberId: editRecord?.id,
            name: values.name,
            provider: formType
          })
        ).unwrap();

        toast.success('Updated successfully');
      } else {
        const payload = { ...values, formType: formType };
        if (formType === 'sip') {
          payload['sip_trunk'] = { id: values?.sip_trunk_id, name: values?.sip_trunk_name };
          delete payload['sip_trunk_id'];
          delete payload['sip_trunk_name'];
        }
        await dispatch(aiPhoneNumberAdd(payload)).unwrap();
        toast.success('Created successfully');
      }

      resetForm();
      onCloseClick(true);
    } catch (error) {
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    onSubmit
  });

  /* ================= EDIT PREFILL ================= */

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        name: editRecord?.name || ''
      });
    }
  }, [isEdit, editRecord, setValues]);

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9+]/g, '');
    if (value.includes('+')) {
      value = '+' + value.replace(/\+/g, '').replace(/^\+/, '');
    }
    setFieldValue('phone_number', value);
  };

  const fetchTrunkOption = async () => {
    try {
      const response = await dispatch(trunkOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setTrunkOptions(options);
      } else {
        setTrunkOptions([]);
      }
    } catch (error) {
      setTrunkOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchTrunkOption();
  }, []);

  /* ================= UI ================= */

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center">{getTitle()}</ModalHeader>

      <ModalBody>
        <form onSubmit={handleSubmit} id="ai-phone-number">
          <Row>
            {isEdit ? (
              /* ================= EDIT MODE ================= */
              <Col lg="12">
                <FormInput
                  name="name"
                  label="Name"
                  placeholder="Enter display name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            ) : (
              /* ================= ADD MODE ================= */
              <>
                {/* ================= TWILIO ================= */}
                {formType === 'twilio' && (
                  <>
                    <Col lg="6">
                      <FormInput
                        name="name"
                        label="Name"
                        placeholder="Enter display name"
                        value={values.name}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="phone_number"
                        label="Phone Number"
                        placeholder="Enter Twilio phone number"
                        value={values.phone_number}
                        onChange={handlePhoneChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="account_sid"
                        label="Account SID"
                        placeholder="Enter Twilio account SID"
                        value={values.account_sid}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="account_token"
                        label="Account Token"
                        placeholder="Enter Twilio account token"
                        value={values.account_token}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                  </>
                )}

                {/* ================= EXOTEL ================= */}
                {formType === 'exotel' && (
                  <>
                    <Col lg="6">
                      <FormInput
                        name="name"
                        label="Name"
                        placeholder="Enter display name"
                        value={values.name}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="exotel_phone_number"
                        label="Phone Number"
                        placeholder="Enter Exotel phone number"
                        value={values.exotel_phone_number}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="exotel_api_key"
                        label="API Key"
                        placeholder="Enter Exotel API key"
                        value={values.exotel_api_key}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="exotel_api_token"
                        label="API Token"
                        placeholder="Enter Exotel API token"
                        value={values.exotel_api_token}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="exotel_subdomain"
                        label="Subdomain"
                        placeholder="Enter Exotel subdomain"
                        value={values.exotel_subdomain}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="exotel_account_sid"
                        label="Account SID"
                        placeholder="Enter Exotel account SID"
                        value={values.exotel_account_sid}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="12">
                      <FormInput
                        name="exotel_app_id"
                        label="App ID"
                        placeholder="Enter Exotel app ID"
                        value={values.exotel_app_id}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                  </>
                )}

                {/* ================= SIP ================= */}
                {formType === 'sip' && (
                  <>
                    <Col lg="6">
                      <FormInput
                        name="name"
                        label="Name"
                        placeholder="Enter display name"
                        value={values.name}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="phone_number"
                        label="Phone Number"
                        placeholder="Enter SIP phone number"
                        value={values.phone_number}
                        onChange={handlePhoneChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="sip_host"
                        label="SIP Host"
                        placeholder="e.g., sip.yourprovider.com"
                        value={values.sip_host}
                        onChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormSelect
                        name="sip_trunk_id"
                        label="SIP Trunk"
                        placeholder="Select SIP trunk"
                        options={trunkOptions}
                        value={values.sip_trunk_id}
                        onChange={(e) => {
                          setFieldValue('sip_trunk_name', e.target.label);
                          setFieldValue('sip_trunk_id', e.target.value);
                        }}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="sip_port"
                        label="SIP Port"
                        placeholder="Default 5060"
                        value={values.sip_port}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="sip_username"
                        label="SIP Username"
                        placeholder="Enter SIP username (optional)"
                        value={values.sip_username}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col lg="6">
                      <FormInput
                        name="sip_password"
                        label="SIP Password"
                        placeholder="Enter SIP password (optional)"
                        value={values.sip_password}
                        onChange={handleChange}
                        type="password"
                      />
                    </Col>
                  </>
                )}
              </>
            )}
          </Row>
        </form>
      </ModalBody>

      <ModalFooter>
        <Button
          className="px-5 bg-transparent text-primary"
          onClick={() => {
            resetForm();
            onCloseClick();
          }}>
          Cancel
        </Button>

        <Button form="ai-phone-number" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditAIPhoneNumberModal.propTypes = {
  isOpen: PropTypes.bool,
  onCloseClick: PropTypes.func,
  isEdit: PropTypes.bool,
  editRecord: PropTypes.any,
  formType: PropTypes.string
};

export default AddEditAIPhoneNumberModal;
